import { useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Plus, ShieldCheck, Trash2, X } from '../../../../../components/icon/solar-icons.js'
import { PortalConfirmDialog } from '../../../../../components/portal-confirm-dialog/portal-confirm-dialog.js'
import { Spinner } from '../../../../../components/spinner/spinner.js'
import {
    createRole as apiCreateRole,
    deleteRole as apiDeleteRole,
    fetchRoles,
    type RoleListItem,
    type RoleScopeType,
} from '../../../utils/portal-api.js'
import { RoleEditor } from './role-editor.js'
import './roles-section.css'

export interface RolesSectionProps {
    lang: Language
    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

export const RolesSection = ({ lang, showToast }: RolesSectionProps) => {
    const [roles, setRoles] = useState<RoleListItem[]>([])
    const [editingId, setEditingId] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)

    // Slide-over state for creation
    const [slideOpen, setSlideOpen] = useState(false)
    const [createName, setCreateName] = useState('')
    const [createDescription, setCreateDescription] = useState('')
    const [createScope, setCreateScope] = useState<RoleScopeType>('everyone')
    const [saving, setSaving] = useState(false)
    const [pendingDelete, setPendingDelete] = useState<{ id: number; memberCount: number } | null>(
        null,
    )

    const reload = async () => {
        setLoading(true)
        try {
            const r = await fetchRoles()
            if (r.ok) setRoles(r.data.roles)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void reload()
    }, [])

    const resetCreateForm = () => {
        setCreateName('')
        setCreateDescription('')
        setCreateScope('everyone')
    }

    const openCreate = () => {
        resetCreateForm()
        setSlideOpen(true)
    }

    const closeCreate = () => {
        setSlideOpen(false)
        resetCreateForm()
    }

    const onSaveCreate = async () => {
        if (!createName.trim()) return
        setSaving(true)
        try {
            const r = await apiCreateRole({
                name: createName,
                description: createDescription || null,
                scope_type: createScope,
            })
            if (r.ok) {
                closeCreate()
                void reload()
            } else {
                showToast(r.error, 'error')
            }
        } finally {
            setSaving(false)
        }
    }

    if (editingId !== null) {
        return (
            <RoleEditor
                lang={lang}
                roleId={editingId}
                showToast={showToast}
                onClose={() => {
                    setEditingId(null)
                    void reload()
                }}
            />
        )
    }

    const tR = t.portal.roles

    return (
        <div className="portal-roles-list">
            <div className="portal-settings__section-header">
                <h3 className="portal-settings__section-title">
                    {t.portal.settings.tabs.roles(lang)}
                </h3>
                <Button
                    variant="primary"
                    size="small"
                    iconLeft={Plus}
                    text={tR.createRole(lang)}
                    onClick={openCreate}
                />
            </div>

            {loading ? (
                <div className="portal-roles-list__loading">
                    <Spinner />
                </div>
            ) : roles.length === 0 ? (
                <div className="portal-settings__section-empty">
                    <ShieldCheck size={24} />
                    <p>{tR.noRoles(lang)}</p>
                </div>
            ) : (
                <div className="portal-roles-list__grid">
                    {roles.map((r) => (
                        <div key={r.id} className="portal-roles-list__row">
                            <div className="portal-roles-list__info">
                                <button
                                    type="button"
                                    className="portal-roles-list__name"
                                    onClick={() => setEditingId(r.id)}
                                >
                                    {r.name}
                                </button>
                                <div className="portal-roles-list__meta">
                                    <span
                                        className={`portal-settings__type-badge portal-settings__type-badge--${r.is_system ? 'static' : 'dynamic'}`}
                                    >
                                        {r.is_system ? tR.typeSystem(lang) : tR.typeCustom(lang)}
                                    </span>
                                    <span>{tR.memberCount(r.member_count)(lang)}</span>
                                </div>
                                {r.description && (
                                    <p className="portal-roles-list__desc">{r.description}</p>
                                )}
                            </div>
                            <div className="portal-settings__data-actions">
                                <button
                                    type="button"
                                    className="portal-settings__action-btn"
                                    onClick={() => setEditingId(r.id)}
                                >
                                    {tR.editRole(lang)}
                                </button>
                                {!r.is_system && (
                                    <button
                                        type="button"
                                        className="portal-settings__action-btn portal-settings__action-btn--danger"
                                        onClick={() =>
                                            setPendingDelete({
                                                id: r.id,
                                                memberCount: r.member_count,
                                            })
                                        }
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {slideOpen ? (
                <>
                    <div className="portal-slide-overlay" onClick={closeCreate} />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">{tR.createRole(lang)}</h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={closeCreate}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={tR.namePlaceholder(lang)}
                                    value={createName}
                                    onChange={(value) => setCreateName(value)}
                                    placeholder={tR.namePlaceholder(lang)}
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <TextField
                                    as="textarea"
                                    label={tR.descriptionPlaceholder(lang)}
                                    value={createDescription}
                                    onChange={(value) => setCreateDescription(value)}
                                    placeholder={tR.descriptionPlaceholder(lang)}
                                    textAreaProps={{ rows: 3 }}
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <label>{tR.scopeSection(lang)}</label>
                                {(['own', 'reports', 'everyone'] as const).map((s) => (
                                    <label key={s} className="portal-role-create__scope-option">
                                        <input
                                            type="radio"
                                            name="create-scope"
                                            checked={createScope === s}
                                            onChange={() => setCreateScope(s)}
                                        />
                                        {tR.scopeOption(s)(lang)}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="portal-slide-over__footer">
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={closeCreate}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={
                                    saving
                                        ? t.portal.settings.customFields.saving(lang)
                                        : t.portal.settings.customFields.save(lang)
                                }
                                isDisabled={saving || !createName.trim()}
                                onClick={onSaveCreate}
                            />
                        </div>
                    </div>
                </>
            ) : null}

            <PortalConfirmDialog
                open={pendingDelete !== null}
                title={t.portal.shared.confirmTitle(lang)}
                message={pendingDelete ? tR.confirmDelete(pendingDelete.memberCount)(lang) : ''}
                confirmLabel={t.portal.shared.confirmDelete(lang)}
                cancelLabel={t.portal.shared.confirmCancel(lang)}
                variant="danger"
                onConfirm={async () => {
                    if (pendingDelete) {
                        const res = await apiDeleteRole(pendingDelete.id)
                        if (res.ok) void reload()
                        else showToast(res.error, 'error')
                    }
                    setPendingDelete(null)
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    )
}
